import type { AnalysisType } from "@/generated/graphql"
import React from "react"
import { View } from "react-native"
import Tooltip from "rn-tooltip"
import BFText from "@/components/standard/BFText"
import ToolTips from "@/constants/ToolTips"
import { BFColors } from "@/constants/Colors"
import InfoIconYellow from "@/assets/icons/tooltip-i-yellow-icon.svg"
import { numberToCurrency, numberWithCommas } from "@/utils/NumberUtils"
import { removeUSAFromString } from "@/utils/StringUtils"
import BedIcon from "@/assets/icons/bed-icon.svg"
import BathIcon from "@/assets/icons/bath-icon.svg"
import AreaIcon from "@/assets/icons/sqft-icon.svg"
import BuiltIcon from "@/assets/icons/year-built-icon.svg"
import EditButton from "@/components/buttons/EditButton"

interface SubjectPropertyOverlayTextProps {
  analysis: AnalysisType
  compactHeaderIsActive: boolean
  onEditPress: () => void
}

export const SubjectPropertyOverlayText: React.FC<
  SubjectPropertyOverlayTextProps
> = ({ analysis, compactHeaderIsActive, onEditPress }) => {
  const property = analysis.property
  const statusUpper: string | null | undefined = property?.status?.toUpperCase()
  const saleInProgress =
    ["ACTIVE", "PENDING", "CONTINGENT"].includes(statusUpper || "") || false

  const iconWidth = 18
  const iconHeight = 14

  const determinedPriceLabel =
    saleInProgress || analysis.priceSelection === "analysis_user_price"
      ? "PURCHASE PRICE"
      : "ESTIMATED AS-IS VALUE"

  const PriceToolTip = ({ className }: { className?: string }) => {
    return (
      <View className={className}>
        <Tooltip
          popover={
            <BFText className="font-spacegrotesk text-white text-[12px] p-[2px]">
              {saleInProgress
                ? ToolTips.purchasePrice
                : ToolTips.estimatedAsIsPrice}
            </BFText>
          }
          actionType="press"
          backgroundColor={BFColors.calmBlue}
          withOverlay={false}
          height={"auto"}
          width={250}
          containerStyle={{
            borderRadius: 2,
          }}
          toggleWrapperProps={{
            hitSlop: 32,
          }}
        >
          <View>
            <InfoIconYellow width={15} height={15} />
          </View>
        </Tooltip>
      </View>
    )
  }

  return (
    <View className="self-stretch">
      {saleInProgress && (
        <BFText className="text-[11px] text-beige font-spacegrotesk leading-none">
          • {statusUpper ?? ""}
        </BFText>
      )}

      <View className="flex flex-row items-center">
        <BFText
          testID="subject-property-purchase-price"
          className="text-white text-[20px] font-figtree-extrabold leading-snug tracking-tight mr-[6px]"
        >
          {analysis.purchasePrice
            ? numberToCurrency(analysis.purchasePrice)
            : "-"}
        </BFText>
        <BFText className="text-[11px] text-white font-spacegrotesk leading-none tracking-tight mt-2 mr-1">
          {determinedPriceLabel}
        </BFText>
        {!compactHeaderIsActive && <PriceToolTip />}
      </View>
      <BFText className="w-full h-[18px] text-white text-[14px] font-figtree-extrabold">
        {removeUSAFromString(property.fullAddress)}
      </BFText>
      <View className="font-spacegrotesk w-full flex-row items-center justify-between">
        <View className="w-[180px] font-spacegrotesk flex-row items-center justify-between mb-2">
          <View className="flex-row items-center">
            <BedIcon width={iconWidth} height={iconHeight} />
            <BFText
              testID="subject-property-bedrooms"
              className="text-white text-[12px] font-spacegrotesk ml-1"
            >
              {analysis.bedrooms || property.bedrooms}
            </BFText>
          </View>
          <View className="flex-row items-center">
            <BathIcon width={iconWidth} height={iconHeight} />
            <BFText
              testID="subject-property-bathrooms"
              className="text-white text-[12px] font-spacegrotesk ml-1"
            >
              {analysis.bathrooms || property.bathrooms}
            </BFText>
          </View>
          <View className="flex-row items-center">
            <AreaIcon width={iconWidth} height={iconHeight} />
            <BFText
              testID="subject-property-area"
              className="text-white text-[12px] font-spacegrotesk ml-1"
            >
              {numberWithCommas(analysis.area || property.area || 0)}
            </BFText>
          </View>
          <View className="flex-row items-center">
            <BuiltIcon width={iconWidth} height={iconHeight} />
            <BFText
              testID="subject-property-year-built"
              className="text-white text-[12px] font-spacegrotesk ml-1"
            >
              {analysis.yearBuilt || property.yearBuilt}
            </BFText>
          </View>
        </View>
        <View className="mr-4">
          <EditButton onPress={onEditPress} isLight={true} />
        </View>
      </View>
    </View>
  )
}
